package com.cs.ejobin.vm

import com.cs.ejobin.app.AppSession
import com.cs.ejobin.data.AuthRepository
import com.cs.ejobin.data.AvatarRepository
import com.cs.ejobin.data.ProfileRepository
import com.cs.ejobin.data.SubscriptionRepository
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.model.Subscription
import com.cs.ejobin.model.UserProfile
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class ProfileState(
    val user: UserProfile? = null,
    val subscription: Subscription = Subscription.None,
    val isLoading: Boolean = false,
    val errorKey: String? = null
)

class ProfileViewModel(
    private val auth: AuthRepository,
    private val profileRepo: ProfileRepository,
    private val avatarRepo: AvatarRepository,
    private val subs: SubscriptionRepository, // gardé pour ne rien casser (mais non utilisé ici)
    private val session: AppSession
) : BaseViewModel() {

    private val _state = MutableStateFlow(ProfileState())
    val state: StateFlow<ProfileState> = _state.asStateFlow()

    fun bindFromSession() {
        val s = session.state.value
        _state.value = _state.value.copy(
            user = s.user,
            // Paiement désactivé : on n’affiche pas/plus une vérité "abonnement"
            subscription = Subscription.None
        )
    }

    fun loadProfile() {
        _state.value = _state.value.copy(isLoading = true, errorKey = null)
        scope.launch {
            runCatching {
                val profile = profileRepo.getMyProfile()
                session.updateUser(profile)
            }.onSuccess {
                bindFromSession()
                _state.value = _state.value.copy(isLoading = false)
            }.onFailure {
                _state.value = _state.value.copy(
                    isLoading = false,
                    errorKey = Keys.error_generic
                )
            }
        }
    }

    /**
     * Paiement désactivé : on laisse la fonction pour éviter de casser
     * le code existant, mais on ne fait rien.
     */
    fun cancelSubscriptionAtPeriodEnd() {
        _state.value = _state.value.copy(
            isLoading = false,
            errorKey = ""
        )
    }

    /**
     * Paiement désactivé : idem.
     */
    fun refreshSubscription() {
        _state.value = _state.value.copy(
            isLoading = false,
            errorKey = ""
        )
    }

    fun updateProfile(
        name: String,
        phone: String,
        isAvailable: Boolean,
        selectedSkills: Set<SkillCatalog>,
        photoUrl: String?,
        description: String,
    ) {
        _state.value = _state.value.copy(isLoading = true, errorKey = null)
        scope.launch {
            runCatching {
                val updated = profileRepo.updateMyProfile(
                    name = name,
                    phone = phone,
                    isAvailable = isAvailable,
                    skills = selectedSkills,
                    photoUrl = photoUrl,
                    description = description
                )
                session.updateUser(updated)
            }.onSuccess {
                bindFromSession()
                _state.value = _state.value.copy(isLoading = false)
            }.onFailure {
                _state.value = _state.value.copy(
                    isLoading = false,
                    errorKey = Keys.error_generic
                )
            }
        }
    }

    fun updateAvatar(
        pickedBytes: ByteArray,
        contentType: String,
        fileExt: String,
        currentName: String,
        currentPhone: String,
        currentAvailable: Boolean,
        currentSkills: Set<SkillCatalog>,
        currentDescription: String,
    ) {
        _state.value = _state.value.copy(isLoading = true, errorKey = null)
        scope.launch {
            runCatching {
                val url = avatarRepo.uploadMyAvatar(pickedBytes, contentType, fileExt)
                val updated = profileRepo.updateMyProfile(
                    name = currentName,
                    phone = currentPhone,
                    isAvailable = currentAvailable,
                    skills = currentSkills,
                    photoUrl = url,
                    description = currentDescription
                )
                session.updateUser(updated)
            }.onSuccess {
                bindFromSession()
                _state.value = _state.value.copy(isLoading = false)
            }.onFailure {
                _state.value = _state.value.copy(
                    isLoading = false,
                    errorKey = Keys.error_generic
                )
            }
        }
    }

    fun logout(onDone: () -> Unit) {
        _state.value = _state.value.copy(isLoading = true, errorKey = null)
        scope.launch {
            runCatching { auth.logout() }
            session.logout()
            _state.value = ProfileState()
            onDone()
        }
    }
}